import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, { buildFaqSchema, buildLocalBusinessSchema } from "@/components/SeoLandingPage";
import { buildBreadcrumbSchema, ldScript, pageHead, SITE_URL } from "@/lib/page-schema";
import heroImage from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-fc-interior.jpg?format=webp&quality=72&w=1400&imagetools";

const PATH = "/our-standard";
const TITLE = "Why Our Atlanta Limo Service Costs More | Empress Elite Standard";
const DESC =
  "What you pay for with Atlanta Luxury Sprinters: we own every vehicle, send background-checked and fingerprinted long-tenured chauffeurs, and run 2025+ custom-built Sprinters.";

const faqs = [
  {
    q: "What does your luxury Sprinter rate include?",
    a: "A vehicle we own, 2025 or newer, with a custom-built interior and detailing before and after your booking. Your chauffeur is a long-tenured member of my team, background-checked and fingerprinted. You know exactly who and what will arrive.",
  },
  {
    q: "Will the vehicle in the photos be the one that arrives?",
    a: "Yes. Our photos are of our own custom-built fleet: the Limo Style Sprinter, the Executive Style Sprinter, the Jet Sprinter and the Suburban SUV. You'll know your vehicle and your chauffeur before pickup, and you can track your chauffeur live in our app.",
  },
];

export const Route = createFileRoute("/our-standard")({
  head: () =>
    pageHead({
      path: PATH,
      title: TITLE,
      description: DESC,
      scripts: [
        ldScript(
          buildLocalBusinessSchema({
            url: `${SITE_URL}${PATH}`,
            name: "Atlanta Luxury Sprinters, The Empress Elite Standard",
            image: `${SITE_URL}/favicon.ico`,
            description: DESC,
            serviceType: "Luxury Chauffeured Transportation",
            areaServed: [{ type: "City", name: "Atlanta" }],
          }),
        ),
        ldScript(buildFaqSchema(faqs)),
        ldScript(
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Our Standard", path: PATH },
          ]),
        ),
      ],
    }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="The Empress Elite Standard"
      h1="Why We Cost More: The Empress Elite Standard"
      intro="You're trusting me with your family, guests or executives. I'll make sure you know the vehicle, the chauffeur and the price before your trip."
      hideQuotePricing
      heroImage={heroImage}
      heroAlt="Custom-built Executive Style Sprinter cabin owned and operated by Atlanta Luxury Sprinters"
      midImage={midImage}
      midAlt="Detailed leather captain seats inside an Atlanta Luxury Sprinters Mercedes-Benz Sprinter"
      sectionOneTitle="What Stands Behind Your Booking"
      sectionOneBody={[
        "Atlanta Luxury Sprinters is the luxury Sprinter fleet of Empress Elite Limousine, my family's company. We're a small, female-owned, Latina-owned business, and we own every vehicle in our fleet.",
        "Your booking brings together a custom-built vehicle, a chauffeur who knows our standards and my personal attention.",
      ]}
      bulletsTitle="The Standard, in Short"
      bulletsIntro="Every booking with Atlanta Luxury Sprinters comes with the same commitments."
      bullets={[
        "We own every vehicle in our fleet",
        "You know what's coming",
        "Background checks and fingerprinting for every chauffeur",
        "Long-tenured chauffeurs",
        "Detailed before and after every job",
        "2025 or newer, custom-built, one of a kind",
        "Investment in safety technology",
        "A small family company where the owner answers",
      ]}
      coverageTitle="Our Fleet"
      coverageIntro="Every vehicle below is ours."
      coverage={["Limo Style Sprinter", "Executive Style Sprinter", "Jet Sprinter", "Suburban SUV"]}
      compareTable={{
        title: "What to Ask Any Limo Company",
        intro: "Ask these four questions before you pay any deposit. Here is how we answer them.",
        headers: ["What to ask any limo company", "Our answer"],
        rows: [
          ["Who owns the vehicle?", "We do. We own every vehicle we send."],
          ["Is the driver background-checked and fingerprinted?", "Yes. Every chauffeur passes background checks and fingerprinting."],
          ["How old is the vehicle?", "2025 or newer, custom-built and one of a kind."],
          ["Is it the exact vehicle in the photos?", "Yes. Our photos show our own fleet."],
        ],
      }}
      detailSections={[
        {
          title: "We Own Every Vehicle in Our Fleet",
          body: [
            "We own every vehicle in our fleet.",
          ],
        },
        {
          title: "You Know What's Coming",
          body: [
            "You'll know your vehicle and your chauffeur before pickup, and you can track your chauffeur live in our app.",
          ],
        },
        {
          title: "Background Checks and Fingerprinting",
          body: [
            "Every chauffeur passes background checks and fingerprinting before driving for us. You are trusting us with your family, your executives and your guests, and we take that seriously.",
          ],
        },
        {
          title: "Long-Tenured Chauffeurs",
          body: [
            "Our chauffeurs are long-tenured with the company. They know our vehicles, our standards and the way our clients expect to be treated.",
          ],
        },
        {
          title: "Detailed Before and After Every Job",
          body: [
            "Every vehicle is detailed before and after every job. You step into a cabin that is ready for you, not one still recovering from the last booking.",
          ],
        },
        {
          title: "2025 or Newer, Custom-Built, One of a Kind",
          body: [
            "Our vehicles are 2025 or newer and custom-built. Each one is one of a kind, which is why you won't see the same cabin anywhere else in Atlanta.",
          ],
        },
        {
          title: "Safety Technology",
          body: ["Track your chauffeur live in our app, so you always know when they'll pull up."],
        },
        {
          title: "A Small Family Company Where the Owner Answers",
          body: [
            "We are a small, family-owned company. When you call or text (678) 431-7222, you reach people who care how your ride goes, and the owner stands behind every booking.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/our-story", title: "Our Story", desc: "A Colombian-owned, family-run limousine company in Atlanta." },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The Mercedes-Benz Sprinter Limousine fleet." },
        { to: "/blog/questions-to-ask-before-paying-a-limo-deposit", title: "9 Questions Before a Deposit", desc: "What to ask any limo company before you pay." },
      ]}
      ctaTitle="Text Me Your Plans"
      ctaBody="Tell me your date, pickup and group size. I'll check availability myself and send your quote in writing."
    />
  );
}
